// The files the assistant may add (G11 PR 2): live's `availableAssets`, the media bin's: the
// brand's library files of every kind and its jobs' delivered outputs, newest first, each once (a
// generation saved into the library as the library's file), the first 50. After a library search
// (live's gallery search, spec assistant-search), the files it found come first. Sent with each
// message as prompt text; the plan's `add_clip` may add only these.
import type { AssetHit, AssistMedia, SourceRef } from "../../../api/generated/model";
import { hitAssets } from "../../../platform/media/asset-search";
import { useLibraryPages } from "../../../platform/media/library-pages";
import { assetItem, mergeNewest, outputItems, readTo, useBrandJobs, type BinItem } from "../bin-sources";
import { serverKnows } from "../length";
import type { FileSource, MediaKind } from "../timeline";

/** G11's cap on the files offered. */
export const MAX_MEDIA = 50;
const MAX_NAME_CHARS = 200;
const KINDS: readonly MediaKind[] = ["video", "image", "audio"];

/** A file the assistant was offered, as the plan names it and as the editor adds it. */
export interface OfferedFile {
  wire: AssistMedia;
  source: FileSource;
  kind: MediaKind;
  durationMs: number | null;
  /** The file's own URL, the bin's, to read a length the library has not recorded; null for none. */
  url: string | null;
}

function wireSource(source: FileSource): SourceRef {
  return source.kind === "asset"
    ? { kind: "asset", asset_id: source.assetId }
    : { kind: "job_output", job_id: source.jobId, output_index: source.index };
}

/** The plan's source as the editor's: the same file, by its key. */
export function offeredKey(source: SourceRef): string {
  return source.kind === "asset" ? `asset:${source.asset_id}` : `output:${source.job_id}:${source.output_index}`;
}

function offered(item: BinItem): OfferedFile | null {
  // An actor is no file of the library (the plan's sources are files).
  if (item.source.kind === "actor") return null;
  const source = item.source;
  const name = Array.from(item.media.name).slice(0, MAX_NAME_CHARS).join("") || "Untitled";
  // A length the server knows, a positive one, as the bin's (`length.ts`); else none, which the
  // assistant's add reads from the file (spec assistant-file-length).
  const durationMs = item.kind !== "image" && serverKnows(item.media.durationMs) ? item.media.durationMs : null;
  return {
    wire: { source: wireSource(source), name, kind: item.kind, ...(durationMs === null ? {} : { duration_ms: durationMs }) },
    source,
    kind: item.kind,
    durationMs,
    url: item.media.url,
  };
}

/** The bin's newest files, merged as the bin merges them, as the assistant is offered them. */
export function offeredFiles(items: readonly BinItem[]): OfferedFile[] {
  return items.flatMap((item) => {
    const file = offered(item);
    return file === null ? [] : [file];
  }).slice(0, MAX_MEDIA);
}

/** A library search's files as the assistant is offered them (spec assistant-search): each
 * file once, as its first hit placed it (a video matches once a scene), the first `limit`. */
export function foundFiles(teamId: string, brandId: string, hits: readonly AssetHit[], limit: number): OfferedFile[] {
  return offeredFiles(hitAssets(hits).map((asset) => assetItem(teamId, brandId, asset))).slice(0, limit);
}

/** A follow-up's files: the ones found first, then the bin's newest not among them, 50 in all. */
export function withFound(found: readonly OfferedFile[], offered: readonly OfferedFile[]): OfferedFile[] {
  const seen = new Set<string>();
  return [...found, ...offered]
    .filter((file) => {
      const key = offeredKey(file.wire.source);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, MAX_MEDIA);
}

/** The files offered now: the library's first page and the jobs' first page, merged newest first. */
export function useAssistMedia(teamId: string, brandId: string): OfferedFile[] {
  const library = useLibraryPages(teamId, brandId, { trash: false }, true, { size: MAX_MEDIA });
  const jobs = useBrandJobs(teamId, brandId);
  const assets = library.data?.pages.flatMap((page) => page.items) ?? [];
  const jobRows = jobs.data?.pages.flatMap((page) => page.items) ?? [];
  // Down to what both first pages cover, so nothing unread comes before what is offered.
  const { items } = mergeNewest([
    {
      items: assets.map((asset) => assetItem(teamId, brandId, asset)),
      readTo: readTo(assets),
      more: (library.data?.pages.at(-1)?.next ?? null) !== null,
    },
    {
      items: jobRows.flatMap((job) => KINDS.flatMap((kind) => outputItems(teamId, job, kind))),
      readTo: readTo(jobRows),
      more: (jobs.data?.pages.at(-1)?.next ?? null) !== null,
    },
  ]);
  return offeredFiles(items);
}
